/**
 * <s-video-clip> — Plays one stretch of a muted video on a seamless loop.
 *
 * Element dataset:
 *   data-start — Clip start, in seconds.
 *   data-end   — Clip end, in seconds. Playback jumps back to data-start here.
 *
 * Required child markup:
 *   video — A muted, playsinline <video>, directly or inside another block.
 *           Without data-end the whole video loops.
 *
 * Plays only while on screen. With reduced motion preferred, it shows the
 * clip's first frame and does not play.
 */
class SVideoClip extends HTMLElement {
  #ac;
  #observer;
  #video;
  #frameHandle;

  connectedCallback() {
    this.#video = this.querySelector('video');
    if (!this.#video) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const video = this.#video;
    video.muted = true;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    video.addEventListener('loadedmetadata', () => {
      video.currentTime = this.#start;
    }, opts);
    if (video.readyState >= 1) video.currentTime = this.#start;

    if (reducedMotion) return;

    video.addEventListener('timeupdate', () => this.#checkEnd(), opts);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#play();
        } else {
          video.pause();
        }
      }
    }, { threshold: 0.25 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    if (this.#frameHandle && this.#video?.cancelVideoFrameCallback) {
      this.#video.cancelVideoFrameCallback(this.#frameHandle);
    }
  }

  get #start() {
    return Number.parseFloat(this.dataset.start) || 0;
  }

  get #end() {
    const end = Number.parseFloat(this.dataset.end);
    return Number.isFinite(end) && end > this.#start ? end : Number.POSITIVE_INFINITY;
  }

  #play() {
    const video = this.#video;
    if (video.currentTime < this.#start || video.currentTime >= this.#end) {
      video.currentTime = this.#start;
    }
    video.play().catch(() => {});
    this.#watchFrames();
  }

  #watchFrames() {
    const video = this.#video;
    if (!video.requestVideoFrameCallback) return;
    const onFrame = () => {
      this.#checkEnd();
      this.#frameHandle = video.requestVideoFrameCallback(onFrame);
    };
    this.#frameHandle = video.requestVideoFrameCallback(onFrame);
  }

  #checkEnd() {
    const video = this.#video;
    if (video.currentTime >= this.#end || video.ended) {
      video.currentTime = this.#start;
      if (video.paused) video.play().catch(() => {});
    }
  }
}

customElements.define('s-video-clip', SVideoClip);
